import React from 'react';

import { UserSession } from '@/api/client';

import type { UserRoles } from '../ability-from-session';

/**
 * Type for the current user session
 * @interface
 * @property {boolean} loading - Boolean indicating if the user session is still loading
 * @property {UserSession | null} user - Current user session or null if not loaded
 * @property {UserRoles | null} roles - Effective roles for presentation checks (override-aware)
 * @property {() => Promise<void>} refreshUser - Function to refresh the user session data (including properties)
 */
interface AuthContextType {
  loading: boolean;
  user: UserSession | null;
  /** Override-aware roles for presentation; `user.roles` remain server truth. */
  roles: UserRoles | null;
  refreshUser: () => Promise<void>;
}

/**
 * Context for the current user session
 * @type {React.Context<AuthContextType>}
 */
const AuthContext = React.createContext<AuthContextType>({
  loading: true,
  user: null,
  roles: null,
  refreshUser: async () => {},
});

/**
 * Custom hook to get the current user session and authentication utilities
 *
 * @returns Object containing:
 *  - loading: Boolean indicating if the user session is still loading
 *  - user: Current user session or null if not loaded
 *  - roles: Effective roles for presentation checks (respects role-baseline overrides)
 *  - refreshUser: Function to refresh the user session data (including properties)
 *
 * @example
 * ```tsx
 * const { user, roles, loading, refreshUser } = useAuth();
 *
 * // Refresh user data after creating a property
 * const handlePropertyCreated = async () => {
 *   await refreshUser();
 * };
 * ```
 */
const useAuth = () => {
  return React.useContext(AuthContext);
};

export { AuthContext, useAuth };
export type { AuthContextType };
